
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Settings, Heart, BookOpen, Shield, ExternalLink, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Sheet, 
  SheetContent, 
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useState } from "react";
import { isStandalone } from "@/utils/device";
import { Badge } from "@/components/ui/badge";
import { getUserInitials } from "@/utils/i18n";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useLanguage } from "@/contexts/language-context";
import logoImage from "@/assets/logo.png";
import subManagerLogo from "@/assets/sub-manager-logo.png";

interface MobileNavProps {
  isLoggedIn: boolean;
  userEmail: string | null;
  isAdmin: boolean;
  handleLogout: () => Promise<void>;
  invitesCount: number;
  isGuardian?: boolean;
  pendingRsvpCount?: number;
  hasTeams?: boolean;
  isParentView?: boolean;
  isDualRole?: boolean;
  managementRoleLabel?: string;
}

export function MobileNav({ 
  isLoggedIn, 
  userEmail, 
  isAdmin, 
  handleLogout,
  invitesCount = 0,
  isGuardian = false,
  pendingRsvpCount = 0,
  hasTeams = true,
  isParentView = false,
  isDualRole = false,
  managementRoleLabel = "Coach",
}: MobileNavProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const isPWA = isStandalone();
  const { t } = useLanguage();
  const initials = getUserInitials(userEmail);
  
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild className="md:hidden">
        <Button variant="ghost" size="icon">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className={`glass-dark text-foreground ${isPWA ? 'safe-area-sheet' : ''}`}>
        <div className="flex items-center space-x-2 mb-6">
          <img 
            src={logoImage} 
            alt="Vote.GameOn Logo" 
            className="h-6 w-6 text-primary"
          />
          <span className="text-xl font-bold">Vote.GameOn</span>
        </div>
        
        {isLoggedIn && userEmail && (
          <>
            <div className="flex items-center gap-3 mb-1 px-2 py-3 rounded-md bg-primary/5">
              <Avatar className="h-10 w-10 border border-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate">{userEmail}</span>
            </div>
            {isDualRole && (
              <div className="flex items-center gap-1 px-2 pb-2 text-xs text-muted-foreground">
                {isParentView ? <Heart className="h-3 w-3" /> : <Shield className="h-3 w-3" />}
                {t('userMenu.viewingAs')}: {isParentView ? t('userMenu.parent') : (managementRoleLabel || 'Coach')}
              </div>
            )}
          </>
        )}
        
        <nav className="flex flex-col space-y-1">
          <Link 
            to="/" 
            className="px-4 py-2 hover:bg-foreground/5 rounded-md"
            onClick={() => setIsOpen(false)}
          >
            {t('navigation.home')}
          </Link>
          {isLoggedIn && (
            <>
              {hasTeams && (
                <>
                  <Link 
                    to="/dashboard" 
                    className="px-4 py-2 hover:bg-foreground/5 rounded-md"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navigation.dashboard')}
                  </Link>
                  <Link 
                    to="/teams" 
                    className="px-4 py-2 hover:bg-foreground/5 rounded-md"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navigation.teams')}
                  </Link>
                  <Link 
                    to="/create" 
                    className="px-4 py-2 hover:bg-foreground/5 rounded-md"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('navigation.createPoll')}
                  </Link>
                  {isAdmin && !isParentView && (
                  <Link 
                    to="/stats" 
                    className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2 relative"
                    onClick={() => setIsOpen(false)}
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Platform</span>
                  </Link>
                  )}
                </>
              )}
              {isGuardian && (
                <Link 
                  to="/parent" 
                  className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2 relative"
                  onClick={() => setIsOpen(false)}
                >
                  <Heart className="h-4 w-4" />
                  <span>{t('navigation.parent')}</span>
                  {pendingRsvpCount > 0 && (
                    <Badge 
                      variant="destructive" 
                      className="h-5 min-w-5 p-0 text-[10px] flex items-center justify-center rounded-full"
                    >
                      {pendingRsvpCount}
                    </Badge>
                  )}
                </Link>
              )}
              <Link
                to="/settings" 
                className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <Settings className="h-4 w-4" />
                <span>{t('navigation.settings')}</span>
              </Link>
              {hasTeams && !isParentView && (
                <Link
                  to="/coach-log"
                  className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2"
                  onClick={() => setIsOpen(false)}
                >
                  <ClipboardList className="h-4 w-4" />
                  <span>Coach Log</span>
                </Link>
              )}
              <Link 
                to="/docs" 
                className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2"
                onClick={() => setIsOpen(false)}
              >
                <BookOpen className="h-4 w-4" />
                <span>{t('navigation.docs')}</span>
              </Link>

              {hasTeams && !isParentView && (
                <div className="pt-4 mt-2 border-t border-border/30">
                  <div className="px-4 pb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Apps
                  </div>
                  <a
                    href="https://club.gameon.futbol/sub-manager"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 hover:bg-foreground/5 rounded-md flex items-center gap-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <img
                      src={subManagerLogo}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4"
                    />
                    <span>Sub Manager</span>
                    <ExternalLink className="h-3 w-3 ms-auto text-muted-foreground" />
                  </a>
                </div>
              )}

              
              <div className="pt-4 mt-2 border-t border-border/30">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }} 
                  className="w-full justify-center gap-1 hover:bg-destructive/10"
                >
                  {t('navigation.logout')}
                </Button>
              </div>
            </>
          )}
          
          {!isLoggedIn && (
            <Button 
              onClick={() => {
                navigate("/login");
                setIsOpen(false);
              }}
              className="w-full mt-4"
            >
              {t('navigation.login')}
            </Button>
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
